import React, { useEffect, useState } from 'react';
import { supabase } from '../../utils/supabase';
import { useAuth } from '../../context/AuthContext';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useDarkMode } from '../../context/DarkModeContext';

interface MultipleFocusWarningProps {
  focusType: string;
  currentFocusId: string;
}

const MultipleFocusWarning: React.FC<MultipleFocusWarningProps> = ({ focusType, currentFocusId }) => {
  const { user } = useAuth();
  const { darkMode } = useDarkMode();
  const [otherFocuses, setOtherFocuses] = useState<any[]>([]);

  useEffect(() => {
    const checkForMultipleFocuses = async () => {
      if (!user?.id || !focusType || !currentFocusId) return;

      const { data, error } = await supabase
        .from('user_career_focuses')
        .select('id, company_name, role, start_date, end_date')
        .eq('user_id', user.id)
        .eq('focus_type', focusType)
        .eq('is_active', true)
        .neq('id', currentFocusId)
        .order('created_at', { ascending: false });

      if (!error && data && data.length > 0) {
        setOtherFocuses(data);
      }
    };

    checkForMultipleFocuses();
  }, [user?.id, focusType, currentFocusId]);

  if (otherFocuses.length === 0) return null;

  const bgColor = darkMode ? 'bg-yellow-900/20' : 'bg-yellow-50';
  const borderColor = darkMode ? 'border-yellow-700' : 'border-yellow-200';
  const textColor = darkMode ? 'text-yellow-300' : 'text-yellow-800';

  return (
    <div className={`p-4 rounded-lg border ${bgColor} ${borderColor} mb-4`}>
      <div className="flex items-start gap-3">
        <ExclamationTriangleIcon className={`w-5 h-5 mt-0.5 ${textColor}`} />
        <div className="flex-1">
          <h4 className={`font-medium ${textColor} mb-2`}>
            Multiple Active {focusType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}s Detected
          </h4>
          <p className={`text-sm ${darkMode ? 'text-yellow-400' : 'text-yellow-700'} mb-3`}>
            You have {otherFocuses.length + 1} active focuses of this type. Currently viewing the most recent one.
          </p>
          <div className="space-y-2">
            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Other active focuses:
            </div>
            {otherFocuses.map(focus => (
              <div key={focus.id} className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                • {focus.company_name || 'Unnamed'} {focus.role ? `- ${focus.role}` : ''} 
                <span className={`text-xs ml-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  ({new Date(focus.start_date).toLocaleDateString()} - {new Date(focus.end_date).toLocaleDateString()})
                </span>
              </div>
            ))}
          </div>
          <p className={`text-xs mt-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            To manage multiple focuses, please deactivate unused ones in the Focus Hub.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MultipleFocusWarning;